import useSound from "use-sound";
import { Howl } from "howler";
import { useMemo } from "react";
import beep from "@/App/Assets/Sound/beep.mp3";
import doorbell from "@/App/Assets/Sound/doorbell.mp3";
import guitar from "@/App/Assets/Sound/guitar.mp3";
import joyous from "@/App/Assets/Sound/joyous.mp3";
import proud from "@/App/Assets/Sound/proud.mp3";
import relax from "@/App/Assets/Sound/relax.mp3";
import upset from "@/App/Assets/Sound/upset.mp3";
import gong from "@/App/Assets/Sound/gong.mp3";
import { AlarmFileSounds, SoundEngineType } from "../utils/enum";
import usePreferredSoundEngine from "../atoms/usePreferredEngineAtom";

const volumeEnum = {
  1: 0.2,
  2: 0.4,
  3: 0.6,
  4: 0.8,
  5: 1,
};

const usePlaySoundHook = (volume = 5) => {
  const [engine] = usePreferredSoundEngine();

  const useSoundMap = {
    [AlarmFileSounds.BEEP]: useSound(beep, { volume: volumeEnum[volume] }),
    [AlarmFileSounds.DOOR_BELL]: useSound(doorbell, {
      volume: volumeEnum[volume],
      html5: true,
    }),
    [AlarmFileSounds.GUITAR]: useSound(guitar, {
      volume: volumeEnum[volume],
      html5: true,
    }),
    [AlarmFileSounds.JOYOUS]: useSound(joyous, {
      volume: volumeEnum[volume],
      html5: true,
    }),
    [AlarmFileSounds.PROUD]: useSound(proud, {
      volume: volumeEnum[volume],
      html5: true,
    }),
    [AlarmFileSounds.RELAX]: useSound(relax, {
      volume: volumeEnum[volume],
      html5: true,
    }),
    [AlarmFileSounds.UPSET]: useSound(upset, {
      volume: volumeEnum[volume],
      html5: true,
    }),
    [AlarmFileSounds.GONG]: useSound(gong, {
      volume: volumeEnum[volume],
      html5: true,
    }),
  };

  const howlerMap = useMemo(
    () => ({
      [AlarmFileSounds.BEEP]: new Audio(beep),
      [AlarmFileSounds.DOOR_BELL]: new Howl({
        src: [doorbell],
        volume: volumeEnum[volume],
        html5: true,
      }),
      [AlarmFileSounds.GUITAR]: new Howl({
        src: [guitar],
        volume: volumeEnum[volume],
        html5: true,
      }),
      [AlarmFileSounds.JOYOUS]: new Howl({
        src: [joyous],
        volume: volumeEnum[volume],
        html5: true,
      }),
      [AlarmFileSounds.PROUD]: new Howl({
        src: [proud],
        volume: volumeEnum[volume],
        html5: true,
      }),
      [AlarmFileSounds.RELAX]: new Howl({
        src: [relax],
        volume: volumeEnum[volume],
        html5: true,
      }),
      [AlarmFileSounds.UPSET]: new Howl({
        src: [upset],
        volume: volumeEnum[volume],
        html5: true,
      }),
      [AlarmFileSounds.GONG]: new Howl({
        src: [gong],
        volume: volumeEnum[volume],
        html5: true,
      }),
    }),
    [volume],
  );

  const playSoundWithUseSound = (soundName: number = AlarmFileSounds.BEEP) => {
    const soundSrc =
      useSoundMap[soundName] || useSoundMap[AlarmFileSounds.BEEP];
    const [play] = soundSrc;
    play();
  };

  const stopWithUseSound = (soundName: number = AlarmFileSounds.BEEP) => {
    const soundSrc =
      useSoundMap[soundName] || useSoundMap[AlarmFileSounds.BEEP];
    const [, { stop }] = soundSrc;
    stop();
  };

  const playSoundWithHowler = (soundName: number = AlarmFileSounds.BEEP) => {
    const sound = howlerMap[soundName] || howlerMap[AlarmFileSounds.BEEP];
    sound.play();
  };

  const stopWithHowler = (soundName: number = AlarmFileSounds.BEEP) => {
    const sound = howlerMap[soundName] || howlerMap[AlarmFileSounds.BEEP];
    sound.stop();
  };

  const playSound =
    engine === SoundEngineType.HOWLER
      ? playSoundWithHowler
      : playSoundWithUseSound;

  const stop =
    engine === SoundEngineType.HOWLER ? stopWithHowler : stopWithUseSound;

  return { playSound, stop };
};

export default usePlaySoundHook;
